import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { closestCenter, DndContext, PointerSensor, useSensor, useSensors, type DragEndEvent } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  GripVertical,
  Play,
  Plus,
  Radio,
  Sparkles,
  Tv,
  X,
} from 'lucide-react'
import { useActiveChannelId } from '../../store/index.js'
import {
  playoutApi,
  type GapFillCampaign,
  type GapFillEntry,
  type GapFillJingle,
  type GapFillOptions,
  type GapFillSpot,
} from '../../lib/api.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import { formatAdSpotLabel } from '../../lib/adSpotLabel.js'
import { formatSpotAirings, orderByLeastAired } from '../../lib/adSpotAirings.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'

export interface GapFillTarget {
  gapSec:        number
  gapStart:      string
  gapEnd:        string | null
  gridId:        string
  previousItemTitle: string | null
  nextItemTitle: string | null
}

interface GapFillDialogProps {
  gap:            GapFillTarget
  availableGaps?: GapFillTarget[]
  onGapChange?:   (gap: GapFillTarget) => void
  onDone:         () => void
  onSkip:         () => void
}

interface CatalogSpot extends GapFillSpot {
  campaignName:   string
  advertiserName: string | null
}

interface SpotEntry extends CatalogSpot {
  rowKey: string
}

/**
 * Le trou se compose comme un montage ordonné : chaque étape est un élément
 * d'antenne, un bloc publicitaire (sa propre liste de spots) ou une boucle
 * d'infoservice. Le backend pose ces étapes dans l'ordre reçu et n'admet qu'un
 * seul infoservice élastique (`fillRest`) pour absorber le temps restant.
 */
type StepKind = 'JINGLE' | 'AD_BREAK' | 'INFOSERVICE'

interface JingleStep      { rowKey: string; kind: 'JINGLE'; programId: string }
interface AdBreakStep     { rowKey: string; kind: 'AD_BREAK'; spots: SpotEntry[] }
interface InfoserviceStep { rowKey: string; kind: 'INFOSERVICE'; fillRest: boolean }
type GapStep = JingleStep | AdBreakStep | InfoserviceStep

const KIND_LABEL: Record<StepKind, string> = {
  JINGLE:      'Élément d’antenne',
  AD_BREAK:    'Bloc pub',
  INFOSERVICE: 'Infoservice',
}
const KIND_TAG: Record<StepKind, string> = { JINGLE: 'JGL', AD_BREAK: 'PUB', INFOSERVICE: 'INFO' }

let rowKeyCounter = 0
const newKey = (prefix: string) => `${prefix}-${++rowKeyCounter}-${Date.now()}`

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

function fmtSec(value: number) {
  const sec = Math.max(0, Math.round(value))
  const h = Math.floor(sec / 3600), m = Math.floor((sec % 3600) / 60), s = sec % 60
  if (h > 0) return `${h}h${String(m).padStart(2, '0')}m${String(s).padStart(2, '0')}s`
  return `${m}m${String(s).padStart(2, '0')}s`
}

function gapKey(gap: GapFillTarget): string {
  return `${gap.gridId}:${gap.gapStart}:${gap.gapEnd ?? ''}`
}

export const GapFillDialog = memo(function GapFillDialog({
  gap,
  availableGaps = [],
  onGapChange,
  onDone,
  onSkip,
}: GapFillDialogProps) {
  const channelId = useActiveChannelId()
  const [options, setOptions] = useState<GapFillOptions | null>(null)
  const [steps, setSteps] = useState<GapStep[]>([])
  const [spotPicker, setSpotPicker] = useState<{ rowKey: string; search: string } | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }))
  const gapDate = useMemo(() => broadcastDayKey(gap.gapStart), [gap.gapStart])

  const gapChoices = useMemo(() => {
    const unique = new Map<string, GapFillTarget>()
    for (const candidate of [gap, ...availableGaps]) unique.set(gapKey(candidate), candidate)
    return [...unique.values()].sort((left, right) => (
      new Date(left.gapStart).getTime() - new Date(right.gapStart).getTime()
    ))
  }, [availableGaps, gap])
  const gapIndex = gapChoices.findIndex(candidate => gapKey(candidate) === gapKey(gap))

  // Repartir d'un montage vierge dès qu'on change de trou : les durées et les
  // spots choisis n'ont plus de sens sur un autre créneau.
  useEffect(() => { setSteps([]); setSpotPicker(null); setError('') }, [gap])

  useEffect(() => {
    if (!channelId) return
    let cancelled = false
    setLoading(true)
    playoutApi.getGapFillOptions(channelId, gapDate)
      .then(result => { if (!cancelled) setOptions(result) })
      .catch(() => { if (!cancelled) setError('Impossible de charger les options') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId, gapDate])

  const jingles = useMemo(
    () => (options?.jingles ?? []).filter(jingle => jingle.mediaAssetId && jingle.durationSec > 0),
    [options?.jingles],
  )
  const jingleById = useMemo(() => new Map(jingles.map(jingle => [jingle.id, jingle])), [jingles])
  const infoservice = options?.infoservice ?? null
  const transitionSec = (options?.transitionDurationMs ?? 0) / 1000

  const catalog = useMemo(() => orderByLeastAired((options?.campaigns ?? []).flatMap((campaign: GapFillCampaign) =>
    campaign.spots.map(spot => ({
      ...spot,
      campaignName: campaign.name,
      advertiserName: campaign.advertiserName,
    })),
  )), [options?.campaigns])

  const adBreakSec = useCallback((spots: SpotEntry[]) => (
    spots.reduce((sum, spot) => sum + spot.durationSec, 0) + Math.max(0, spots.length - 1) * transitionSec
  ), [transitionSec])

  // Durée réclamée par chaque étape ; `null` = infoservice élastique qui prend
  // le temps restant. Tout le calcul de remplissage en découle.
  const requested = useMemo(() => steps.map((step): number | null => {
    if (step.kind === 'JINGLE') return jingleById.get(step.programId)?.durationSec ?? 0
    if (step.kind === 'AD_BREAK') return adBreakSec(step.spots)
    return step.fillRest ? null : infoservice?.durationSec ?? 0
  }), [adBreakSec, infoservice?.durationSec, jingleById, steps])

  const fixedSec = requested.reduce<number>((sum, value) => sum + (value ?? 0), 0)
  const elasticCount = requested.filter(value => value === null).length
  const remaining = gap.gapSec - fixedSec
  const isOverfill = remaining < 0
  const multipleElastic = elasticCount > 1
  const elasticTooShort = elasticCount === 1 && remaining < 1
  const filledSec = fixedSec + (elasticCount > 0 ? Math.max(0, remaining) : 0)
  const pct = Math.min(100, Math.round((filledSec / gap.gapSec) * 100))

  const stepReady = useCallback((step: GapStep) => (
    step.kind === 'JINGLE'   ? jingleById.has(step.programId)
    : step.kind === 'AD_BREAK' ? step.spots.length > 0
    : Boolean(infoservice)
  ), [infoservice, jingleById])
  const allReady = steps.length > 0 && steps.every(stepReady)

  const canSave = Boolean(
    channelId && gap.gapEnd && allReady && !isOverfill && !multipleElastic && !elasticTooShort && !saving,
  )

  const displaySec = useCallback((index: number): number => {
    const value = requested[index]
    return value === null ? Math.max(0, remaining) : value
  }, [remaining, requested])

  // `position` place l'étape en tête (juste après le programme précédent) ou en
  // fin (juste avant le programme suivant) du montage.
  const addStep = useCallback((kind: StepKind, position: 'start' | 'end' = 'end') => {
    setError('')
    const insert = (step: GapStep) => setSteps(prev => (position === 'start' ? [step, ...prev] : [...prev, step]))
    if (kind === 'JINGLE') {
      insert({ rowKey: newKey('jgl'), kind, programId: jingles[0]?.id ?? '' })
      return
    }
    if (kind === 'INFOSERVICE') {
      // Un seul infoservice peut être élastique : le nouvel arrivant ne prend le
      // temps restant que si aucun autre ne le fait déjà.
      const rest = !steps.some(step => step.kind === 'INFOSERVICE' && step.fillRest)
      insert({ rowKey: newKey('info'), kind, fillRest: rest })
      return
    }
    const rowKey = newKey('pub')
    insert({ rowKey, kind, spots: [] })
    setSpotPicker({ rowKey, search: '' })
  }, [jingles, steps])

  const removeStep = useCallback((rowKey: string) => {
    setSteps(prev => prev.filter(step => step.rowKey !== rowKey))
    setSpotPicker(picker => (picker?.rowKey === rowKey ? null : picker))
  }, [])
  const moveStep = useCallback((index: number, direction: -1 | 1) => {
    setSteps(prev => {
      const target = index + direction
      return target < 0 || target >= prev.length ? prev : arrayMove(prev, index, target)
    })
  }, [])
  const setJingle = useCallback((rowKey: string, programId: string) => {
    setSteps(prev => prev.map(step => (step.rowKey === rowKey && step.kind === 'JINGLE' ? { ...step, programId } : step)))
  }, [])
  const setFillRest = useCallback((rowKey: string, fillRest: boolean) => {
    setSteps(prev => prev.map(step => {
      if (step.kind !== 'INFOSERVICE') return step
      if (step.rowKey === rowKey) return { ...step, fillRest }
      // Activer l'élasticité sur une étape la retire des autres.
      return fillRest ? { ...step, fillRest: false } : step
    }))
  }, [])

  const addSpot = useCallback((rowKey: string, spot: CatalogSpot) => {
    setSteps(prev => prev.map(step => (
      step.rowKey === rowKey && step.kind === 'AD_BREAK'
        ? { ...step, spots: [...step.spots, { ...spot, rowKey: newKey('spot') }] }
        : step
    )))
    setSpotPicker(null)
  }, [])
  const removeSpot = useCallback((rowKey: string, spotKey: string) => {
    setSteps(prev => prev.map(step => (
      step.rowKey === rowKey && step.kind === 'AD_BREAK'
        ? { ...step, spots: step.spots.filter(spot => spot.rowKey !== spotKey) }
        : step
    )))
  }, [])
  const reorderSpots = useCallback((rowKey: string, from: number, to: number) => {
    setSteps(prev => prev.map(step => (
      step.rowKey === rowKey && step.kind === 'AD_BREAK'
        ? { ...step, spots: arrayMove(step.spots, from, to) }
        : step
    )))
  }, [])

  const changeGap = useCallback((direction: -1 | 1) => {
    const next = gapChoices[gapIndex + direction]
    if (next && onGapChange) onGapChange(next)
  }, [gapChoices, gapIndex, onGapChange])

  const handleSave = useCallback(async () => {
    if (!canSave || !channelId || !gap.gapEnd) return
    const gapEnd = gap.gapEnd
    const items: GapFillEntry[] = steps.map(step => {
      if (step.kind === 'JINGLE') {
        const jingle = jingleById.get(step.programId) as GapFillJingle
        return { itemType: 'JINGLE', programId: jingle.id, mediaAssetId: jingle.mediaAssetId ?? undefined }
      }
      if (step.kind === 'AD_BREAK') return { itemType: 'AD_BREAK', spotIds: step.spots.map(spot => spot.id) }
      return { itemType: 'INFOSERVICE', fillRest: step.fillRest }
    })

    setSaving(true)
    setError('')
    try {
      // Un comblement pose souvent plusieurs éléments d'un coup. S'il ne convient
      // pas, l'opérateur doit pouvoir les retirer ensemble ; ceux déjà passés à
      // l'antenne, eux, resteront en place.
      await recordGridChange({
        label:   'comblement d’un trou',
        gridIds: [gap.gridId],
        date:    broadcastDayKey(gap.gapStart),
      }, () => playoutApi.fillGap(channelId, {
        gridId: gap.gridId,
        startTime: gap.gapStart,
        endTime: gapEnd,
        items,
      }))
      onDone()
    } catch (exception: any) {
      setError(exception?.message ?? 'Erreur lors de l\'insertion')
    } finally {
      setSaving(false)
    }
  }, [canSave, channelId, gap, jingleById, onDone, steps])

  const statusState = isOverfill || multipleElastic || elasticTooShort ? 'danger' : remaining === 0 ? 'success' : 'info'
  const statusDetail = isOverfill ? `Dépassement de ${fmtSec(-remaining)}`
    : multipleElastic ? 'Un seul infoservice peut occuper le temps restant'
    : elasticTooShort ? 'Temps restant trop court pour l’infoservice'
    : elasticCount > 0 ? <>L’infoservice absorbe {fmtSec(Math.max(0, remaining))}</>
    : remaining > 0 ? `Reste ${fmtSec(remaining)} libre` : <><Check aria-hidden="true" /> Trou comblé pile</>

  return (
    <ModalShell className="agenda-editor gap-fill" closeDisabled={saving} dismissOnOverlay={false} onClose={onSkip} showCloseButton={false} size="wide" title="Remplissage du trou">
      <div className="gap-fill__body">
        <div className="gap-fill__summary">
          <div className="gap-fill__switcher" role="group" aria-label="Trou à remplir">
            {gapChoices.length > 1 && (
              <button
                type="button"
                className="gap-fill__switcher-arrow"
                onClick={() => changeGap(-1)}
                disabled={saving || gapIndex <= 0 || !onGapChange}
                aria-label="Trou précédent"
                title="Trou précédent"
              >
                <ChevronLeft aria-hidden="true" />
              </button>
            )}
            <div className="gap-fill__switcher-body">
              <span className="gap-fill__switcher-eyebrow">
                {gapChoices.length > 1 ? `Trou ${gapIndex + 1} / ${gapChoices.length}` : 'Trou à remplir'}
              </span>
              <span className="gap-fill__switcher-range">
                {fmtTime(gap.gapStart)} → {gap.gapEnd ? fmtTime(gap.gapEnd) : '?'} · {fmtSec(gap.gapSec)}
              </span>
            </div>
            {gapChoices.length > 1 && (
              <button
                type="button"
                className="gap-fill__switcher-arrow"
                onClick={() => changeGap(1)}
                disabled={saving || gapIndex >= gapChoices.length - 1 || !onGapChange}
                aria-label="Trou suivant"
                title="Trou suivant"
              >
                <ChevronRight aria-hidden="true" />
              </button>
            )}
          </div>
          <div className={`gap-fill__status is-${statusState}`} aria-live="polite">
            <div className="gap-fill__status-value">{fmtSec(filledSec)} / {fmtSec(gap.gapSec)}</div>
            <div className="gap-fill__status-detail">{statusDetail}</div>
          </div>
        </div>

        <div
          className="gap-fill__progress"
          data-state={statusState}
          role="progressbar"
          aria-label="Durée du trou remplie"
          aria-valuemin={0}
          aria-valuemax={gap.gapSec}
          aria-valuenow={Math.min(gap.gapSec, Math.max(0, filledSec))}
        >
          <div className="gap-fill__progress-value" style={{ '--gap-fill-progress': `${pct}%` } as CSSProperties} />
        </div>

        <div className="gap-fill__montage">
          {loading ? (
            <div className="agenda-editor__loading"><Spinner /> Chargement…</div>
          ) : (
            <>
              <div className="gap-fill__boundary gap-fill__boundary--prev">
                <span className="gap-fill__boundary-role">Programme précédent</span>
                <span className="gap-fill__boundary-label">
                  <Circle aria-hidden="true" /> {gap.previousItemTitle ?? 'Élément précédent'}
                </span>
                <span className="gap-fill__boundary-time">se termine à {fmtTime(gap.gapStart)}</span>
              </div>

              <button
                type="button"
                className="gap-fill__edge-add"
                disabled={jingles.length === 0}
                onClick={() => addStep('JINGLE', 'start')}
                title="Insérer un jingle en tête du montage"
              >
                <Plus aria-hidden="true" /> Jingle après le programme précédent
              </button>

              <div className="gap-fill__steps" role="list">
                {steps.length === 0 ? (
                  <div className="gap-fill__empty">
                    Le trou est vide. Ajoutez un élément d’antenne, un bloc pub ou un infoservice ci-dessous
                    pour le composer.
                  </div>
                ) : steps.map((step, index) => (
                  <StepCard
                    key={step.rowKey}
                    step={step}
                    index={index}
                    isLast={index === steps.length - 1}
                    durationSec={displaySec(index)}
                    sharePct={Math.min(100, displaySec(index) / gap.gapSec * 100)}
                    ready={stepReady(step)}
                    jingles={jingles}
                    infoservice={infoservice}
                    sensors={sensors}
                    catalog={catalog}
                    spotPicker={spotPicker?.rowKey === step.rowKey ? spotPicker : null}
                    onOpenPicker={() => setSpotPicker({ rowKey: step.rowKey, search: '' })}
                    onClosePicker={() => setSpotPicker(null)}
                    onPickerSearch={value => setSpotPicker({ rowKey: step.rowKey, search: value })}
                    onMoveUp={() => moveStep(index, -1)}
                    onMoveDown={() => moveStep(index, 1)}
                    onRemove={() => removeStep(step.rowKey)}
                    onJingle={value => setJingle(step.rowKey, value)}
                    onFillRest={value => setFillRest(step.rowKey, value)}
                    onAddSpot={spot => addSpot(step.rowKey, spot)}
                    onRemoveSpot={spotKey => removeSpot(step.rowKey, spotKey)}
                    onReorderSpots={(from, to) => reorderSpots(step.rowKey, from, to)}
                  />
                ))}
              </div>

              <button
                type="button"
                className="gap-fill__edge-add"
                disabled={jingles.length === 0}
                onClick={() => addStep('JINGLE', 'end')}
                title="Insérer un jingle en fin de montage"
              >
                <Plus aria-hidden="true" /> Jingle avant le programme suivant
              </button>

              <div className="gap-fill__add" role="group" aria-label="Ajouter au montage">
                <span className="gap-fill__add-label"><Plus aria-hidden="true" /> Ajouter</span>
                <div className="gap-fill__add-buttons">
                  <button type="button" className="gap-fill__add-button" disabled={jingles.length === 0} onClick={() => addStep('JINGLE')}>
                    <Tv aria-hidden="true" /> Élément d’antenne
                  </button>
                  <button type="button" className="gap-fill__add-button" onClick={() => addStep('AD_BREAK')}>
                    <Radio aria-hidden="true" /> Bloc pub
                  </button>
                  <button type="button" className="gap-fill__add-button" disabled={!infoservice} onClick={() => addStep('INFOSERVICE')}>
                    <Sparkles aria-hidden="true" /> Infoservice
                  </button>
                </div>
              </div>

              <div className="gap-fill__boundary gap-fill__boundary--next">
                <span className="gap-fill__boundary-role">Programme suivant</span>
                <span className="gap-fill__boundary-label">
                  <Play aria-hidden="true" /> {gap.nextItemTitle ?? 'Prochain programme'}
                </span>
                <span className="gap-fill__boundary-time">démarre à {gap.gapEnd ? fmtTime(gap.gapEnd) : '?'}</span>
              </div>
            </>
          )}
        </div>

        {error && <div className="agenda-editor__notice is-danger" role="alert"><AlertTriangle aria-hidden="true" /> {error}</div>}

        <div className="agenda-editor__actions gap-fill__actions">
          <button type="button" className="tp-button" onClick={onSkip} disabled={saving}>Ignorer le trou</button>
          <button type="button" className="tp-button tp-button--info" data-state={saving ? 'loading' : undefined} onClick={handleSave} disabled={!canSave}>
            {!saving && <Check aria-hidden="true" />}
            {saving ? 'Insertion…' : 'Confirmer le remplissage'}
          </button>
        </div>
      </div>
    </ModalShell>
  )
})

interface StepCardProps {
  step:          GapStep
  index:         number
  isLast:        boolean
  durationSec:   number
  sharePct:      number
  ready:         boolean
  jingles:       GapFillJingle[]
  infoservice:   GapFillOptions['infoservice']
  sensors:       ReturnType<typeof useSensors>
  catalog:       CatalogSpot[]
  spotPicker:    { rowKey: string; search: string } | null
  onOpenPicker:  () => void
  onClosePicker: () => void
  onPickerSearch:(value: string) => void
  onMoveUp:      () => void
  onMoveDown:    () => void
  onRemove:      () => void
  onJingle:      (value: string) => void
  onFillRest:    (value: boolean) => void
  onAddSpot:     (spot: CatalogSpot) => void
  onRemoveSpot:  (spotKey: string) => void
  onReorderSpots:(from: number, to: number) => void
}

const StepCard = memo(function StepCard({
  step, index, isLast, durationSec, sharePct, ready, jingles, infoservice, sensors, catalog,
  spotPicker, onOpenPicker, onClosePicker, onPickerSearch,
  onMoveUp, onMoveDown, onRemove, onJingle, onFillRest, onAddSpot, onRemoveSpot, onReorderSpots,
}: StepCardProps) {
  const kind = step.kind
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    if (step.kind !== 'AD_BREAK' || !event.over || event.active.id === event.over.id) return
    const from = step.spots.findIndex(spot => spot.rowKey === event.active.id)
    const to = step.spots.findIndex(spot => spot.rowKey === event.over?.id)
    if (from >= 0 && to >= 0) onReorderSpots(from, to)
  }, [onReorderSpots, step])

  const filteredCatalog = useMemo(() => {
    const query = (spotPicker?.search ?? '').trim().toLocaleLowerCase('fr-FR')
    if (!query) return catalog
    return catalog.filter(spot => [spot.title, spot.campaignName, spot.advertiserName]
      .some(value => value?.toLocaleLowerCase('fr-FR').includes(query)))
  }, [catalog, spotPicker?.search])

  const title = kind === 'JINGLE'
    ? jingles.find(jingle => jingle.id === step.programId)?.title ?? 'Élément d’antenne à choisir'
    : kind === 'AD_BREAK'
      ? `Bloc pub · ${step.spots.length} spot${step.spots.length > 1 ? 's' : ''}`
      : infoservice?.title ?? 'Infoservice'

  return (
    <div
      className="gap-fill__step"
      role="listitem"
      data-kind={kind.toLowerCase()}
      data-ready={ready || undefined}
      style={{ '--gap-fill-share': `${sharePct}` } as CSSProperties}
    >
      <div className="gap-fill__step-bar" aria-hidden="true" />
      <div className="gap-fill__step-main">
        <div className="gap-fill__step-head">
          <span className={`gap-fill__kind is-${kind.toLowerCase()}`}>{KIND_TAG[kind]}</span>
          <span className="gap-fill__step-title">{title}</span>
          <span className="gap-fill__step-duration">{fmtSec(durationSec)}</span>
          <div className="gap-fill__step-actions">
            <button type="button" className="gap-fill__step-button" onClick={onMoveUp} disabled={index === 0} aria-label={`Monter ${KIND_LABEL[kind]}`} title="Monter"><ChevronUp aria-hidden="true" /></button>
            <button type="button" className="gap-fill__step-button" onClick={onMoveDown} disabled={isLast} aria-label={`Descendre ${KIND_LABEL[kind]}`} title="Descendre"><ChevronDown aria-hidden="true" /></button>
            <button type="button" className="gap-fill__step-button gap-fill__step-button--remove" onClick={onRemove} aria-label={`Retirer ${KIND_LABEL[kind]}`} title="Retirer"><X aria-hidden="true" /></button>
          </div>
        </div>

        {kind === 'JINGLE' && (
          <label className="gap-fill__step-field">
            <span>Élément d’antenne</span>
            <select className="tp-input" value={step.programId} onChange={event => onJingle(event.target.value)}>
              <option value="">Choisir…</option>
              {jingles.map(jingle => <option key={jingle.id} value={jingle.id}>{jingle.title} · {fmtSec(jingle.durationSec)}</option>)}
            </select>
          </label>
        )}

        {kind === 'INFOSERVICE' && infoservice && (
          <div className="gap-fill__step-field gap-fill__step-field--info">
            <div className="gap-fill__info-meta">
              Sélection active{infoservice.mediaDate ? ` · ${infoservice.mediaDate}` : ''}
              {' · '}média de {fmtSec(infoservice.durationSec)} par passage
            </div>
            <label className="gap-fill__info-toggle">
              <input type="checkbox" checked={step.fillRest} onChange={event => onFillRest(event.target.checked)} />
              <span>Occuper tout le temps restant et prolonger la plage voisine si elle touche le trou</span>
            </label>
          </div>
        )}

        {kind === 'AD_BREAK' && (
          <div className="gap-fill__step-field">
            {step.spots.length === 0 ? (
              <div className="agenda-editor__empty agenda-editor__empty--compact">Ajoutez les spots du bloc publicitaire.</div>
            ) : (
              <DndContext sensors={sensors} collisionDetection={closestCenter} modifiers={[restrictToVerticalAxis]} onDragEnd={handleDragEnd}>
                <SortableContext items={step.spots.map(spot => spot.rowKey)} strategy={verticalListSortingStrategy}>
                  <div className="ad-spot-list gap-fill__spot-list">
                    {step.spots.map((spot, spotIndex) => (
                      <GapSpotRow
                        key={spot.rowKey}
                        spot={spot}
                        index={spotIndex}
                        isLast={spotIndex === step.spots.length - 1}
                        onMoveUp={() => onReorderSpots(spotIndex, spotIndex - 1)}
                        onMoveDown={() => onReorderSpots(spotIndex, spotIndex + 1)}
                        onRemove={() => onRemoveSpot(spot.rowKey)}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            {spotPicker ? (
              <GapSpotPicker
                catalog={filteredCatalog}
                search={spotPicker.search}
                onSearchChange={onPickerSearch}
                onPick={onAddSpot}
                onClose={onClosePicker}
              />
            ) : (
              <button type="button" className="ad-spot-add-trigger gap-fill__spot-add" onClick={onOpenPicker}><Plus aria-hidden="true" /> Ajouter un spot</button>
            )}
          </div>
        )}
      </div>
    </div>
  )
})

const GapSpotRow = memo(function GapSpotRow({ spot, index, isLast, onMoveUp, onMoveDown, onRemove }: {
  spot: SpotEntry
  index: number
  isLast: boolean
  onMoveUp: () => void
  onMoveDown: () => void
  onRemove: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: spot.rowKey })
  return (
    <div ref={setNodeRef} className={`ad-spot-row${isDragging ? ' is-dragging' : ''}`} style={{ transform: CSS.Transform.toString(transform), transition }}>
      <div className="ad-spot-row__index">{index + 1}</div>
      <button type="button" aria-label={`Déplacer ${formatAdSpotLabel(spot)}`} title="Déplacer" {...attributes} {...listeners} className="ad-spot-row__drag"><GripVertical aria-hidden="true" /></button>
      <div className="ad-spot-row__main">
        <div className="ad-spot-row__title">{formatAdSpotLabel(spot)}</div>
        <div className="ad-spot-row__meta">
          {formatSpotAirings(spot.airingsCount)}{spot.advertiserName ? ` · ${spot.advertiserName}` : ''}
        </div>
      </div>
      <div className="ad-spot-row__duration">{fmtSec(spot.durationSec)}</div>
      <div className="ad-spot-row__actions">
        <button type="button" className="ad-spot-row__button" onClick={onMoveUp} disabled={index === 0} aria-label={`Monter ${formatAdSpotLabel(spot)}`} title="Monter"><ChevronUp aria-hidden="true" /></button>
        <button type="button" className="ad-spot-row__button" onClick={onMoveDown} disabled={isLast} aria-label={`Descendre ${formatAdSpotLabel(spot)}`} title="Descendre"><ChevronDown aria-hidden="true" /></button>
        <button type="button" className="ad-spot-row__button ad-spot-row__button--remove" onClick={onRemove} aria-label={`Retirer ${formatAdSpotLabel(spot)}`} title="Retirer"><X aria-hidden="true" /></button>
      </div>
    </div>
  )
})

const GapSpotPicker = memo(function GapSpotPicker({ catalog, search, onSearchChange, onPick, onClose }: {
  catalog: CatalogSpot[]
  search: string
  onSearchChange: (value: string) => void
  onPick: (spot: CatalogSpot) => void
  onClose: () => void
}) {
  return (
    <div className="ad-spot-picker ad-spot-picker--embedded gap-fill__spot-picker">
      <div className="ad-spot-picker__toolbar">
        <input autoFocus value={search} onChange={event => onSearchChange(event.target.value)} placeholder="Filtrer titre, campagne, annonceur…" className="tp-input ad-spot-picker__search" />
        <button type="button" onClick={onClose} className="tp-button">Fermer</button>
      </div>
      <div className="ad-spot-picker__list">
        {catalog.length === 0 ? (
          <div className="agenda-editor__empty agenda-editor__empty--compact">Aucun spot disponible.</div>
        ) : catalog.map(spot => (
          <button key={spot.id} type="button" className="ad-spot-picker__option" onClick={() => onPick(spot)}>
            <div className="ad-spot-picker__main">
              <div className="ad-spot-picker__title">{formatAdSpotLabel(spot)}</div>
              <div className="ad-spot-picker__meta">
                {formatSpotAirings(spot.airingsCount)}{spot.advertiserName ? ` · ${spot.advertiserName}` : ''}
              </div>
            </div>
            <div className="ad-spot-picker__duration">{fmtSec(spot.durationSec)}</div>
          </button>
        ))}
      </div>
    </div>
  )
})
